import { getBaseURL } from "../Utilities/Site";

interface PendoVisitorOptions {
    id: string;
    user_name: string;
    role: string;
    esri_user_name: string;
    associated_labor: string;
    opengov_identity_email: string;
    is_integration_user: boolean;
}

interface PendoAccountOptions {
    id: string;
    orgUrl: string;
    cg1Url: string;
    name: string;
    instance_id: string;
    instance_type: string;
}

interface Metadata {
    [key: string]: string | number | boolean | string[];
}

interface EventCallbacks {
    ready?(): void;
    guidesLoaded?(): void;
    guidesFailed?(): void;
}

interface PendoInstance {
    initialize: (opts: { visitor?: PendoVisitorOptions; account: PendoAccountOptions; events?: EventCallbacks; }) => void;
    clearSession: () => void;
    track: (trackType: string, metadata?: Metadata) => void;
}

export class PendoService {
    static pendo: PendoInstance;

    static async init(clientData: any, userAccount: any, cgOneWebUrl: string) {
        // If we haven't yet fetched the API, do so now
        if (!PendoService.pendo) PendoService.pendo = await downloadPendo();

        if (!PendoService.pendo) return;

        if (!userAccount) {
            PendoService.pendo.clearSession();
            return;
        }

        const opts = {
            visitor: PendoService.getVisitor(clientData, userAccount),
            account: PendoService.getAccount(clientData, cgOneWebUrl)
        };

        try {
            await PendoService.initPendoAsync(opts);
            PendoService.trackLogin();
            PendoService.setupPendoButton();
        } catch (e) {
            // tslint:disable-next-line: no-console
            console.warn("Failed to initialize Pendo");
        }
    }

    /** Since Pendo init is actually async, but they don't expose it as a promise
     * let's wrap the callback to make it a nice, awaitable promise
     */
    static initPendoAsync(opts: { visitor?: PendoVisitorOptions; account: PendoAccountOptions }) {
        return new Promise<void>((resolve, reject) => {
            PendoService.pendo.initialize({
                ...opts,
                events: { ready: () => resolve() }
            });

            // If the call takes over 10 seconds, just forget it
            setTimeout(reject, 10000);
        });
    }

    static getVisitor(clientData: any, userAccount: any) {
        const userName = userAccount.UserName ? userAccount.UserName.toLowerCase() : "anonymousir";
        return {
            id: clientData.InstanceID + "|" + userName,
            email: userAccount.Email,
            user_name: userAccount.UserName,
            role: userAccount.Role,
            first_name: userAccount.FirstName,
            last_name: userAccount.LastName,
            esri_user_name: userAccount.EsriUsername,
            associated_labor: userAccount.LaborRecord,
            opengov_identity_email: userAccount.OpenGovEmail,
            is_integration_user: userAccount.IsIntegrationUser
        };
    }

    static getAccount(clientData: any, cgOneWebUrl: string): PendoAccountOptions {
        return {
            id: clientData.InstanceID + "(" + clientData.Account + ")", // "I-563 (City of Springfield)"
            name: clientData.Account, // "City of Springfield"
            instance_id: clientData.InstanceID, // "I-563"
            instance_type: clientData.InstanceType, // "Production"
            orgUrl: getBaseURL(),
            cg1Url: cgOneWebUrl // "https://release.cartegraph.app"
        };
    }

    static trackLoginType(authenticationType: "Cartegraph" | "Esri" | "Internal Request") {
        window.localStorage.setItem("authenticationType", authenticationType);
    }

    static trackLogin() {
        const authenticationType = window.localStorage.getItem("authenticationType");
        if (!authenticationType) return;

        // https://developers.pendo.io/docs/?bash#track-events
        PendoService.pendo.track("Login", { authenticationType });
        window.localStorage.removeItem("authenticationType");
    }

    static trackDuplicateResource(duplicateEquipment: number, duplicateLabor: number, duplicateMaterial: number, taskList: string[]): void {
        PendoService.pendo.track("DuplicateLEMODistribution", {
            equipment: duplicateEquipment,
            labor: duplicateLabor,
            material: duplicateMaterial,
            tasks: taskList
        });
    }

    // Use MutationObserver to listen when the #pendo-resource-center-container element
    // gets added/removed from the DOM.  Certain links within Resource Center will close
    // the dialog automatically and the button that launches the Resource Center becomes
    // out of sync with it's highlighted/active state.
    static setupPendoButton() {
        const selector = "#pendo-resource-center-container";
        const $pendoBtn = $("#cg-pendo-spacer");

        if (document.querySelector(selector)) {
            $pendoBtn.addClass("active");
            return;
        }

        const observer = new MutationObserver(mutations => {
            if (document.querySelector(selector)) {
                $pendoBtn.addClass("active");
                return;
            }
            mutations.forEach((mutation) => {
                mutation.removedNodes.forEach((removedNode) => {
                    if ((removedNode as any).id === "pendo-resource-center-container") {
                        const $pendoBtn = $("#cg-pendo-spacer");
                        $pendoBtn.removeClass("active");
                        return;
                    }
                });
            });
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true
        });
    }
}

// tslint:disable
// prettier-ignore
function downloadPendo(): Promise<PendoInstance> {
  // ************ Begin Pendo Snippet ****************
  (function(apiKey){
        (function(p: any,e: any,n: any,d: any,o?:any){var v: any,w,x,y: any,z;o=p[d]=p[d]||{};o._q=[];
            v=['initialize','identify','updateOptions','pageLoad'];for(w=0,x=v.length;w<x;++w)(function(m){
                o[m]=o[m]||function(){o._q[m===v[0]?'unshift':'push']([m].concat([].slice.call(arguments,0)));};})(v[w]);
            y=e.createElement(n);y.async=!0;y.src='https://cdn.pendo.io/agent/static/'+apiKey+'/pendo.js';
            z = e.getElementsByTagName(n)[0]; z.parentNode.insertBefore(y, z);
        })(window, document, 'script', 'pendo');
    })('2a4c925c-19c3-4722-7abb-14e7cc2ec151');
  // ************ End Pendo Snippet ****************

  return new Promise((resolve) => {
    var x = 0;
    // Wait until the pendo script has loaded
    // (5s max), then return the window.pendo object
    var intervalId = setInterval(() => {
      if ((window as any).pendo
        && typeof (window as any).pendo.isReady === "function") {
        clearInterval(intervalId);
        resolve((window as any).pendo);
      } else if (++x >= 10) {
        clearInterval(intervalId);
        resolve(null);
      }
    }, 500);
  });
}

